import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import DocumentRef from "./DocumentRef";
import COLORS from "../colors";
import { deepFreeze, formatCurrency, timestampToDate } from "../util/misc";
import { cloneDeep, every, get, isEmpty } from "lodash";
import { diff } from "deep-object-diff";

export const EXPENSE_FIELDS = {
  // When the purchase was made (the date on the receipt), which need not be
  // the day the expense is submitted. ExpenseEdit defaults it to today for a
  // new expense; expenses that predate the field carry null.
  date: null,
  description: "",
  amount: null,
  category: "",
  // Materials taken from our own stock rather than bought for the job. Only
  // meaningful for the "Materials" category. Informational for now (the expense
  // still counts toward cost exactly the same); stock levels will be drawn down
  // from this flag once stock is managed in depth.
  fromStock: false,
  projectID: null,
  saleID: null,
  receipt: null,
  mileageLogIds: null,
  status: "pending",
  reviewedBy: null,
  reviewedAt: null,
  submittedBy: null,
  submittedAt: null,

  // Meta info
  created: null,
  createdBy: null,
  modified: null,
  modifiedBy: null,
};
deepFreeze(EXPENSE_FIELDS);

export const EXPENSE_STATUS_TYPES = {
  pending: {
    order: 1,
    label: "Pending",
    icon: "pending",
    color: "warning",
  },
  reviewed: {
    order: 2,
    label: "Reviewed",
    icon: "done",
    color: "positive",
  },
};
deepFreeze(EXPENSE_STATUS_TYPES);

export const DEFAULT_EXPENSE_CATEGORIES = [
  "Advertising/Marketing",
  "Equipment Rental",
  "Fuel",
  "Labor",
  "Materials",
  "Meals",
  "Mileage Reimbursement",
  "Office Supplies",
  "Other",
  "Shipping",
  "Tools",
  "Travel",
];
deepFreeze(DEFAULT_EXPENSE_CATEGORIES);

export default class Expense extends DataStructure {
  constructor(id, data = {}) {
    data = sanitize(data, EXPENSE_FIELDS);

    // Convert to Date
    data.date = timestampToDate(data.date);
    data.created = timestampToDate(data.created);
    data.modified = timestampToDate(data.modified);
    data.submittedAt = timestampToDate(data.submittedAt);
    data.reviewedAt = timestampToDate(data.reviewedAt);

    super(data);

    this.id = id || null;
    this.permissions = Object.freeze({
      view: Expense.canBeViewedBy,
      create: Expense.canBeCreatedBy,
      edit: this.canBeEditedBy,
      review: this.canBeReviewedBy,
      delete: this.canBeDeletedBy,
    });

    // Documents
    if (this.data.receipt) {
      this.data.receipt = this.receipt.data;
    }
  }

  // Permissions

  static canBeViewedBy(user) {
    return user.isEmployee;
  }

  static canBeCreatedBy(user) {
    return user.isEmployee;
  }

  canBeEditedBy(user) {
    // Can edit if pending and user is submitter, admin, or accounting
    return (
      this.data.status === "pending" &&
      (user.id === this.data.submittedBy ||
        user.isAdmin ||
        user.hasRole("accounting"))
    );
  }

  canBeReviewedBy(user) {
    return user.isAdmin || user.hasRole("accounting");
  }

  canBeDeletedBy(user) {
    // Only pending expenses can be deleted, by admin, accounting, or submitter
    return (
      this.data.status === "pending" &&
      (user.isAdmin ||
        user.hasRole("accounting") ||
        user.id === this.data.submittedBy)
    );
  }

  // Database

  get path() {
    return Expense.getPath(this.id);
  }

  static getPath(id) {
    let path = `expenses`;
    if (id) {
      path += `/${id}`;
    }
    return path;
  }

  static toFirestore(expense) {
    // Convert undefined values to null for Firestore compatibility
    const data = { ...expense.data };
    Object.keys(data).forEach((key) => {
      if (data[key] === undefined) {
        data[key] = null;
      }
    });
    return data;
  }

  static fromFirestore(snapshot, options) {
    const data = snapshot.data(options);
    return new Expense(snapshot.id, data);
  }

  // Editing

  get rules() {
    return {
      date: [(a) => Boolean(a) || "required"],
      description: [(a) => Boolean(a) || "required"],
      amount: [(a) => (a !== null && a >= 0) || "amountInvalid"],
      category: [(a) => Boolean(a) || "required"],
      receipt: [
        (a) =>
          store.state.main.user.hasRole("accounting") ||
          this.data.category === "Mileage Reimbursement" ||
          Boolean(a && this.receipt && this.receipt.isValid) ||
          "required",
      ],
    };
  }

  get isValidForSubmit() {
    const data = Expense.sanitize(cloneDeep(this.data));
    return every(this.rules, (rules, key) =>
      rules.every((rule) => rule(get(data, key)) === true)
    );
  }

  get isValidForReview() {
    return this.isValidForSubmit;
  }

  get isValid() {
    return this.isValidForSubmit;
  }

  get isEmpty() {
    // `date` is defaulted to today on a fresh buffer, so on its own it is not
    // a draft worth keeping (same treatment as Transaction.isEmpty).
    return Boolean(
      !this.id &&
        isEmpty(
          diff(
            Expense.sanitize({ ...this.data, status: "pending", date: null }),
            EXPENSE_FIELDS
          )
        )
    );
  }

  isEqual(expense) {
    return Boolean(
      this.id === expense.id && isEmpty(diff(this.data, expense.data))
    );
  }

  static cleanDocuments(data, removeUnsaved = false) {
    if (data.receipt) {
      const doc = new DocumentRef(data.receipt);
      if (!doc[removeUnsaved ? "isSaved" : "isValid"]) {
        data.receipt = null;
      }
    }
    return data;
  }

  static sanitize(data, removeUnsaved) {
    Expense.cleanDocuments(data, removeUnsaved);
    return data;
  }

  // Formatting

  get receipt() {
    return this.data.receipt ? new DocumentRef(this.data.receipt) : null;
  }

  get amount() {
    return this.data.amount !== null ? formatCurrency(this.data.amount) : null;
  }

  // True only for material expenses drawn from stock; `fromStock` is ignored on
  // every other category so a stale flag left by a category change can't leak
  // into the UI.
  get isFromStock() {
    return Boolean(this.data.category === "Materials" && this.data.fromStock);
  }

  get submittedBy() {
    return this.data.submittedBy
      ? store.state.main.users[this.data.submittedBy]
      : null;
  }

  get reviewedBy() {
    return this.data.reviewedBy
      ? store.state.main.users[this.data.reviewedBy]
      : null;
  }

  get createdBy() {
    return this.data.createdBy
      ? store.state.main.users[this.data.createdBy]
      : null;
  }

  get modifiedBy() {
    return this.data.modifiedBy
      ? store.state.main.users[this.data.modifiedBy]
      : null;
  }

  get sale() {
    if (this.data.projectID && this.data.saleID) {
      const saleId = this.data.projectID + this.data.saleID;
      return store.state.main.sales[saleId];
    }
    return null;
  }

  get statusType() {
    const type = EXPENSE_STATUS_TYPES[this.data.status];
    return type ? { ...type, ...COLORS[type.color] } : null;
  }

  get po() {
    if (this.data.projectID && this.data.saleID) {
      return this.data.projectID + this.data.saleID;
    }
    return null;
  }

  toString() {
    return this.data.description || this.category;
  }
}
